import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent } from '@theme/events';

/**
 * A custom element that pairs a number input with minus/plus buttons.
 *
 * Reconstructed against the contract its callers already depend on, after the
 * shipped file was overwritten with a copy of the cart subclass and left
 * importing QuantitySelectorComponent from itself — a module that imports its
 * own missing export, so it threw and neither quantity selector ever defined.
 *
 * The contract, for anyone extending this:
 *   markup    snippets/quantity-selector.liquid — refs minusButton /
 *             quantityInput / plusButton, and the on:click, on:blur and
 *             on:focus routes below.
 *   PDP       assets/product-form.js calls updateConstraints, setCartQuantity,
 *             getValue and setValue when the variant changes.
 *   cart      assets/component-cart-items.js calls updateCartQuantity and
 *             updateButtonStates after every cart mutation, and listens for the
 *             QuantitySelectorUpdateEvent dispatched here.
 *   subclass  assets/component-cart-quantity-selector.js overrides
 *             getEffectiveMax and updateButtonStates.
 *
 * @extends {Component<HTMLElement>}
 */
export class QuantitySelectorComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.updateButtonStates();
  }

  /**
   * The input this component drives. Deliberately not a requiredRef: a missing
   * input should leave one dead stepper on the page, not throw out of
   * connectedCallback and take every later component on the page with it.
   * @returns {HTMLInputElement | null}
   */
  get #input() {
    const input = this.refs.quantityInput;
    return input instanceof HTMLInputElement ? input : null;
  }

  /**
   * Current numeric state of the input. max is null when the input carries no
   * max attribute at all, which means unlimited rather than zero.
   * @returns {{min: number, max: number | null, step: number, value: number}}
   */
  getCurrentValues() {
    const input = this.#input;
    if (!input) return { min: 1, max: null, step: 1, value: 1 };

    const rawMax = input.getAttribute('max');

    return {
      min: this.#toNumber(input.getAttribute('min'), 1),
      max: rawMax === null || rawMax === '' ? null : this.#toNumber(rawMax, 0),
      step: Math.max(1, this.#toNumber(input.getAttribute('step'), 1)),
      value: this.#toNumber(input.value, 1),
    };
  }

  /**
   * How high this selector may go.
   *
   * On a product form the input asks "how many to ADD", so whatever is already
   * in the cart eats into the variant's limit. The cart subclass overrides this
   * because there the input asks "what should the total BE".
   *
   * @returns {number | null} null when there is no ceiling.
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    if (max === null) return null;

    return max - this.#cartQuantity();
  }

  /** @returns {number} The current input value. */
  getValue() {
    return this.getCurrentValues().value;
  }

  /**
   * Writes a value without announcing it. Callers that want the cart to react
   * use the stepper methods instead.
   * @param {number | string} value
   */
  setValue(value) {
    if (!this.#input) return;
    this.#input.value = String(value);
    this.updateButtonStates();
  }

  /**
   * Re-reads the cart quantity the cart wrote onto the input, then re-evaluates
   * the buttons against it.
   */
  updateCartQuantity() {
    this.updateButtonStates();
  }

  /**
   * @param {number} quantity - Units of this variant already in the cart.
   */
  setCartQuantity(quantity) {
    if (!this.#input) return;
    this.#input.setAttribute('data-cart-quantity', String(quantity));
    this.updateButtonStates();
  }

  /**
   * Applies a new variant's quantity rules, then snaps the standing value into
   * the new bounds so the input is never left in a state it would reject.
   * @param {string | number} min
   * @param {string | number | null} max
   * @param {string | number} step
   */
  updateConstraints(min, max, step) {
    const input = this.#input;
    if (!input) return;

    input.setAttribute('min', String(min));
    input.setAttribute('step', String(step));
    if (max === null || max === '') input.removeAttribute('max');
    else input.setAttribute('max', String(max));

    input.value = String(this.#clamp(this.getCurrentValues().value));
    this.updateButtonStates();
  }

  /**
   * Greys out a button once its direction has nowhere left to go. A server-set
   * disabled attribute (a variant that cannot be ordered at all) is left alone.
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    if (minusButton instanceof HTMLButtonElement) minusButton.disabled = value <= min;
    if (plusButton instanceof HTMLButtonElement) {
      plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
    }
  }

  /**
   * @param {Event} [event]
   */
  decreaseQuantity(event) {
    event?.preventDefault();
    const { value, step } = this.getCurrentValues();
    this.#commit(value - step);
  }

  /**
   * @param {Event} [event]
   */
  increaseQuantity(event) {
    event?.preventDefault();
    const { value, step } = this.getCurrentValues();
    this.#commit(value + step);
  }

  /**
   * Typed-in values land here on blur. An empty or nonsense entry falls back to
   * the minimum rather than sending NaN at the cart.
   */
  setQuantity() {
    this.#commit(this.getCurrentValues().value);
  }

  /**
   * Selecting the text on focus means typing replaces the quantity instead of
   * appending a digit to it.
   */
  selectInputValue() {
    this.#input?.select();
  }

  /**
   * Clamps, writes, repaints the buttons and tells the cart. The event carries
   * the line number so cart-items can address the right row; a product form has
   * no line and cart-items ignores the event on that basis.
   * @param {number} quantity
   */
  #commit(quantity) {
    const input = this.#input;
    if (!input) return;

    const next = this.#clamp(quantity);
    input.value = String(next);
    this.updateButtonStates();

    const line = this.#toNumber(input.getAttribute('data-cart-line'), 0);
    this.dispatchEvent(new QuantitySelectorUpdateEvent(next, line || undefined));
  }

  /**
   * Snaps a value onto a valid increment above the minimum, then holds it
   * between the min and the effective max.
   * @param {number} value
   * @returns {number}
   */
  #clamp(value) {
    const { min, step } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    // 0 is how the cart says "remove this line", so it survives the floor
    if (value <= 0) return 0;

    let next = Math.max(min, value);
    if (step > 1) next = min + Math.round((next - min) / step) * step;
    if (effectiveMax !== null && effectiveMax >= min) next = Math.min(next, effectiveMax);

    return Math.max(min, next);
  }

  /** @returns {number} Units of this variant already in the cart. */
  #cartQuantity() {
    return this.#toNumber(this.#input?.getAttribute('data-cart-quantity') ?? null, 0);
  }

  /**
   * @param {string | null} raw
   * @param {number} fallback
   * @returns {number}
   */
  #toNumber(raw, fallback) {
    const parsed = Number.parseInt(raw ?? '', 10);
    return Number.isNaN(parsed) ? fallback : parsed;
  }
}

if (!customElements.get('quantity-selector-component')) {
  customElements.define('quantity-selector-component', QuantitySelectorComponent);
}
